import React from 'react'
import { Flex, Tag, Tooltip, Typography } from 'antd'

/**
 * Compact two-group view of a section set.
 *
 * A grant can carry ~38 sections, so listing each as a tag overwhelms both the grid and the drawer.
 * Grouping collapses that to two slugs and keeps the names one hover away, which is enough for the
 * question this panel answers: how much does this person hold, and is any of it sensitive.
 *
 * The split is driven by the `sensitiveSections` list the API returns, never by a local constant, so
 * the grouping cannot disagree with the backend's own classification.
 */

export const SECTION_GROUP_LABELS = {
  base: 'Base',
  sensitive: 'Sensitive',
} as const

export type SectionGroups = {
  /** Sections where the department does not narrow access. */
  base: string[]
  /** Sections where the department-routed General/HC distinction is mandatory. */
  sensitive: string[]
}

export const splitSectionsByGroup = (
  sections: string[],
  sensitiveSections: string[],
): SectionGroups => ({
  base: sections.filter(section => !sensitiveSections.includes(section)),
  sensitive: sections.filter(section => sensitiveSections.includes(section)),
})

/** Joined section names for the hover, sorted so the tooltip is stable. */
const toTooltip = (sections: string[]) => [...sections].sort().join(', ')

type TagsProps = {
  sections: string[]
  sensitiveSections: string[]
  /** Shown when the set is empty. Varies by context, e.g. HC sets say more than "None". */
  emptyContent?: React.ReactNode
}

export const SectionGroupTags: React.FC<TagsProps> = ({
  sections,
  sensitiveSections,
  emptyContent = <Typography.Text type="secondary">None</Typography.Text>,
}) => {
  if (!sections.length) {
    return <>{emptyContent}</>
  }

  const { base, sensitive } = splitSectionsByGroup(sections, sensitiveSections)

  return (
    <Flex gap={4} wrap="wrap">
      {base.length
        ? (
          <Tooltip title={toTooltip(base)}>
            <Tag color="blue" className="m-0">
              {SECTION_GROUP_LABELS.base} {base.length}
            </Tag>
          </Tooltip>
        )
        : null}
      {sensitive.length
        ? (
          <Tooltip title={toTooltip(sensitive)}>
            <Tag color="red" className="m-0">
              {SECTION_GROUP_LABELS.sensitive} {sensitive.length}
            </Tag>
          </Tooltip>
        )
        : null}
    </Flex>
  )
}

/**
 * Explains the two slugs. Rendered once per surface rather than per row, which is the point of
 * grouping in the first place.
 */
export const SectionGroupLegend: React.FC = () => (
  <Flex vertical gap={4}>
    <Flex gap="small" align="center" wrap="wrap">
      <Tag color="blue" className="m-0">{SECTION_GROUP_LABELS.base} n</Tag>
      <Typography.Text type="secondary">
        Sections where the employee&apos;s department does not narrow access.
      </Typography.Text>
    </Flex>
    <Flex gap="small" align="center" wrap="wrap">
      <Tag color="red" className="m-0">{SECTION_GROUP_LABELS.sensitive} n</Tag>
      <Typography.Text type="secondary">
        Sections where the General / HC split is mandatory
      </Typography.Text>
    </Flex>
    <Typography.Text type="secondary" className="text-xs">
      The number is how many sections are in that group. Hover a slug for their names.
    </Typography.Text>
  </Flex>
)
